import * as _ from 'lodash-es';
import React, { Fragment } from 'react';
import { MenuItem, MenuSeparator } from '../../components/Menu';
import { gql } from '../../graphql/generated';
import type { DocumentType } from '../../graphql/generated';
import addIcon from '../../icons/non-prefilled/16x16/add.svg';
import collapseIcon from '../../icons/non-prefilled/16x16/collapse.svg';
import expandIcon from '../../icons/non-prefilled/16x16/expand.svg';
import historyIcon from '../../icons/non-prefilled/16x16/history.svg';
import newTabIcon from '../../icons/non-prefilled/16x16/newTab.svg';
import textIcon from '../../icons/non-prefilled/16x16/text.svg';
import trashIcon from '../../icons/non-prefilled/16x16/trash.svg';
import topicPath from '../../utils/url/topicPath';

const TOPIC_FRAGMENT = gql(`
  fragment TopicTreeCardMenu_topic on Topic {
    id
    ...topicPath_topic
  }
`);

export interface TopicTreeCardMenuCallbacks {
  onExpandedChange: (recursive: boolean) => void;
  onSubtopicCreate: (params: { topicTempId: string; parentId: string }) => void;
  onTopicDeletedChange: (params: { id: string; deleted: boolean }) => void;
}

interface TopicTreeCardMenuProps extends TopicTreeCardMenuCallbacks {
  isReorganizing: boolean;
  deleted: boolean;
  header: boolean;
  topic: DocumentType<typeof TOPIC_FRAGMENT>;
  hasSubtopics: boolean;
  hasPosts: boolean;
  expanded: boolean;
  onRenameButtonClick: () => void;
}

const TopicTreeCardMenu = ({
  isReorganizing,
  deleted,
  header,
  topic,
  hasSubtopics,
  hasPosts,
  expanded,
  onExpandedChange,
  onSubtopicCreate,
  onTopicDeletedChange,
  onRenameButtonClick,
}: TopicTreeCardMenuProps) => {
  const menuItems = [];

  if (!isReorganizing) {
    menuItems.push(
      <MenuItem
        key="viewTopicPage"
        icon={newTabIcon}
        to={topicPath(topic)}
        newTab
      >
        View topic page
      </MenuItem>,
    );
  }

  if (!deleted && !header) {
    menuItems.push(
      <MenuItem
        key="collapse"
        icon={expanded ? collapseIcon : expandIcon}
        disabled={!hasSubtopics && !hasPosts}
        onClick={() => onExpandedChange(true)}
      >
        {expanded ? 'Collapse' : 'Expand'}
      </MenuItem>,
    );
  }

  if (isReorganizing && deleted) {
    menuItems.push(
      <MenuItem
        key="restore"
        icon={historyIcon}
        onClick={() => onTopicDeletedChange({ id: topic.id, deleted: false })}
      >
        Restore
      </MenuItem>,
    );
  }

  if (isReorganizing && !deleted) {
    if (menuItems.length) {
      menuItems.push(<MenuSeparator key="separator" />);
    }
    menuItems.push(
      <Fragment key="normalActions">
        <MenuItem
          icon={addIcon}
          onClick={() => {
            onSubtopicCreate({
              topicTempId: _.uniqueId(),
              parentId: topic.id,
            });
            if (!expanded) onExpandedChange(false);
          }}
        >
          Create subtopic
        </MenuItem>
        <MenuItem icon={textIcon} onClick={() => onRenameButtonClick()}>
          Rename
        </MenuItem>
        <MenuSeparator />
        <MenuItem
          icon={trashIcon}
          danger
          onClick={() => onTopicDeletedChange({ id: topic.id, deleted: true })}
        >
          Delete topic
        </MenuItem>
      </Fragment>,
    );
  }

  return <>{menuItems}</>;
};

export default TopicTreeCardMenu;
